import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import ScanInboundLog from "@/components/communication/ScanInboundLog";
import { toast } from "sonner";
import { Buildings, Plus, SignOut, Users, House, ChartBar, Power } from "@phosphor-icons/react";

const EMPTY_NEW = {
  tenant: {
    name: "",
    display_name: "",
    legal_name: "",
    address_street: "",
    address_zip: "",
    address_city: "",
    email: "",
    phone: "",
  },
  admin_email: "",
  admin_password: "",
  admin_name: "",
};

export default function Mandanten() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(EMPTY_NEW);

  const fetchTenants = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tenants");
      setTenants(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  const createTenant = async (e) => {
    e.preventDefault();
    if (!draft.tenant.name || !draft.tenant.display_name) {
      toast.error("Bitte Kurzname und Anzeigename ausfüllen");
      return;
    }
    if (!draft.admin_email || !draft.admin_password || draft.admin_password.length < 6) {
      toast.error("Admin-Daten unvollständig (Passwort min. 6 Zeichen)");
      return;
    }
    try {
      await api.post("/tenants", draft);
      toast.success("Mandant angelegt");
      setOpen(false);
      setDraft(EMPTY_NEW);
      fetchTenants();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const suspend = async (t) => {
    if (!confirm(`Mandant „${t.display_name}" sperren? Alle User werden deaktiviert.`)) return;
    try {
      await api.delete(`/tenants/${t.id}`);
      toast.success("Mandant gesperrt");
      fetchTenants();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const totalUnits = tenants.reduce((s, t) => s + (t.units_count || 0), 0);
  const totalProps = tenants.reduce((s, t) => s + (t.properties_count || 0), 0);
  const totalUsers = tenants.reduce((s, t) => s + (t.users_count || 0), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 text-slate-900">
      {/* Top bar */}
      <div className="h-14 border-b border-slate-200 bg-white flex items-center px-3 sm:px-6 gap-3">
        <img src="/logo.svg" alt="Domivus" className="w-8 h-8" />
        <div className="font-semibold tracking-tight text-sm sm:text-base">
          <span className="hidden sm:inline">Domivus · </span>Mandantenzentrale
        </div>
        <div className="ml-auto flex items-center gap-3">
          <div className="text-right hidden sm:block">
            <div className="text-sm font-medium leading-tight truncate max-w-[180px]">{user?.name}</div>
            <div className="text-[11px] text-slate-500 leading-tight truncate max-w-[180px]">{user?.email}</div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={async () => { await logout(); navigate("/login"); }}
            data-testid="platform-logout"
          >
            <SignOut size={16} className="mr-1.5" /> <span className="hidden sm:inline">Abmelden</span>
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Mandanten</h1>
            <p className="text-slate-500 mt-1 text-sm">Verwalte alle Hausverwaltungen, die Domivus nutzen.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="new-tenant-btn">
                <Plus size={16} className="mr-1.5" /> Neuer Mandant
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Neuen Mandanten anlegen</DialogTitle>
              </DialogHeader>
              <form onSubmit={createTenant} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Kurzname (URL-Slug)</Label>
                    <Input
                      placeholder="z. B. hv-mueller"
                      value={draft.tenant.name}
                      onChange={(e) => setDraft({ ...draft, tenant: { ...draft.tenant, name: e.target.value.toLowerCase() } })}
                      data-testid="tenant-name"
                    />
                  </div>
                  <div>
                    <Label>Anzeigename</Label>
                    <Input
                      placeholder="HV Müller GmbH"
                      value={draft.tenant.display_name}
                      onChange={(e) => setDraft({ ...draft, tenant: { ...draft.tenant, display_name: e.target.value } })}
                      data-testid="tenant-display-name"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Firmenname (rechtlich)</Label>
                    <Input
                      value={draft.tenant.legal_name}
                      onChange={(e) => setDraft({ ...draft, tenant: { ...draft.tenant, legal_name: e.target.value } })}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Straße + Hausnr.</Label>
                    <Input
                      value={draft.tenant.address_street}
                      onChange={(e) => setDraft({ ...draft, tenant: { ...draft.tenant, address_street: e.target.value } })}
                    />
                  </div>
                  <div>
                    <Label>PLZ</Label>
                    <Input
                      value={draft.tenant.address_zip}
                      onChange={(e) => setDraft({ ...draft, tenant: { ...draft.tenant, address_zip: e.target.value } })}
                    />
                  </div>
                  <div>
                    <Label>Ort</Label>
                    <Input
                      value={draft.tenant.address_city}
                      onChange={(e) => setDraft({ ...draft, tenant: { ...draft.tenant, address_city: e.target.value } })}
                    />
                  </div>
                  <div>
                    <Label>Telefon</Label>
                    <Input
                      value={draft.tenant.phone}
                      onChange={(e) => setDraft({ ...draft, tenant: { ...draft.tenant, phone: e.target.value } })}
                    />
                  </div>
                  <div>
                    <Label>E-Mail (Firma)</Label>
                    <Input
                      type="email"
                      value={draft.tenant.email}
                      onChange={(e) => setDraft({ ...draft, tenant: { ...draft.tenant, email: e.target.value } })}
                    />
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-4">
                  <div className="font-medium mb-2">Erster Administrator (super_admin)</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <Label>Name</Label>
                      <Input
                        value={draft.admin_name}
                        onChange={(e) => setDraft({ ...draft, admin_name: e.target.value })}
                        data-testid="admin-name"
                      />
                    </div>
                    <div>
                      <Label>E-Mail</Label>
                      <Input
                        type="email"
                        value={draft.admin_email}
                        onChange={(e) => setDraft({ ...draft, admin_email: e.target.value.toLowerCase() })}
                        data-testid="admin-email"
                      />
                    </div>
                    <div>
                      <Label>Passwort</Label>
                      <Input
                        type="password"
                        value={draft.admin_password}
                        onChange={(e) => setDraft({ ...draft, admin_password: e.target.value })}
                        data-testid="admin-password"
                      />
                    </div>
                  </div>
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                    Abbrechen
                  </Button>
                  <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-tenant-btn">
                    Anlegen
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-8">
          <KpiCard icon={Buildings} label="Mandanten" value={tenants.length} />
          <KpiCard icon={House} label="Objekte gesamt" value={totalProps} />
          <KpiCard icon={ChartBar} label="Einheiten gesamt" value={totalUnits} />
          <KpiCard icon={Users} label="User gesamt" value={totalUsers} />
        </div>

        <div className="mb-8"><ScanInboundLog platform /></div>

        {/* Tenant cards */}
        {loading ? (
          <div className="text-slate-500">Lade…</div>
        ) : tenants.length === 0 ? (
          <div className="text-center py-16 text-slate-500">
            Noch keine Mandanten. Lege den ersten an.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {tenants.map((t) => (
              <div
                key={t.id}
                className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                data-testid={`tenant-card-${t.name}`}
                onClick={() => navigate(`/mandanten/${t.id}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter") navigate(`/mandanten/${t.id}`); }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    {t.logo_url ? (
                      <img src={t.logo_url} alt={t.display_name} className="h-10 w-10 object-contain rounded-sm border border-slate-200 bg-white p-0.5 shrink-0" />
                    ) : (
                      <div className="h-10 w-10 rounded-sm bg-slate-900 text-white flex items-center justify-center font-bold text-xs shrink-0">
                        {(t.display_name || "?").slice(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="text-lg font-semibold tracking-tight truncate">{t.display_name}</div>
                      <div className="text-sm text-slate-500 truncate">{t.legal_name || t.name}</div>
                    </div>
                  </div>
                  <Badge
                    variant={t.status === "active" ? "default" : "secondary"}
                    className={t.status === "active" ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : ""}
                  >
                    {t.status === "active" ? "Aktiv" : t.status === "suspended" ? "Gesperrt" : t.status}
                  </Badge>
                </div>
                <div className="mt-4 grid grid-cols-4 gap-2 text-xs">
                  <Stat label="User" value={t.users_count} />
                  <Stat label="Objekte" value={t.properties_count} />
                  <Stat label="Einheiten" value={t.units_count} />
                  <Stat label="Aktive MV" value={t.active_tenancies} />
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <div className="text-xs text-slate-500 flex-1">
                    {t.address_city ? `${t.address_zip || ""} ${t.address_city}`.trim() : ""}
                  </div>
                  {t.status === "active" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); suspend(t); }}
                      data-testid={`suspend-${t.name}`}
                    >
                      <Power size={14} className="mr-1" /> Sperren
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center">
        <Icon size={20} className="text-slate-700" />
      </div>
      <div>
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-xl font-semibold">{value}</div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-md px-2 py-1.5 text-center">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="font-semibold text-slate-800">{value ?? 0}</div>
    </div>
  );
}
